<script setup lang="ts">
    import { type SmallCardTyping } from '@/typings/widget';
    import SmallCard from './SmallCard.ce.vue';

    const props = defineProps<{
        modelValue: number | number[]
        title?: string
        subtitle?: string
        items?: SmallCardTyping[]
        multiple?: boolean
        noHeader?: boolean
        skip?: boolean
        skipText?: string
        alignCenter?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: number | number[]): void
        (event: 'next' | 'skip'): void
    }>();

    const { t } = useI18n();

    const cardsModel: Ref<number | number[] | null> = ref(props.modelValue ?? null);

    const handleSelectedCard = async(card: SmallCardTyping): Promise<void> => {
        if (props.multiple) {
            const cardIndex = (cardsModel.value as number[]).findIndex(crd => crd === card.id);
            if (cardIndex > -1) {
                (cardsModel.value as number[]).splice(cardIndex, 1);
            } else {
                (cardsModel.value as number[]).push(card.id);
            }
        // eslint-disable-next-line no-negated-condition
        } else {
            cardsModel.value = card.id;
            await nextTick(() => {
                emit('next');
            });
        }
    };

    watch(() => cardsModel.value, nVal => {
        if (nVal) emit('update:modelValue', nVal);
    });
</script>

<template>
    <div
        :class="{
            'small-cards--multiple': (cardsModel as number[])?.length > 0 && multiple,
            'small-cards--no-header': noHeader
        }"
        class="small-cards"
    >
        <div class="small-cards__header">
            <div class="small-cards__title">
                {{ title }}
            </div>
            <div
                v-if="subtitle"
                class="small-cards__subtitle"
            >
                {{ subtitle }}
            </div>
        </div>
        <div
            :class="{
                'small-cards__container--wide': noHeader,
                'small-cards__container--centered': alignCenter
            }"
            class="small-cards__container"
        >
            <SmallCard
                v-for="(card, index) in items"
                :key="`${card.title}-${index}`"
                :card="card"
                :selected="multiple ? (cardsModel as number[]).includes(card.id) : cardsModel === card.id"
                @click="handleSelectedCard(card)"
            />
        </div>
        <button
            v-if="(cardsModel as number[])?.length > 0 && multiple"
            type="button"
            class="btn btn--primary small-cards__next"
            @click="emit('next')"
        >
            <span>
                {{ t('widget.buttons.next') }}
            </span>
        </button>
        <button
            v-if="skip"
            type="button"
            class="btn btn--primary small-cards__skip"
            @click="emit('skip')"
        >
            <span>
                {{ skipText }}
            </span>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.small-cards {
    position: relative;
    display: flex;
    flex-direction: column;
    padding-block-start: var(--spacing-03);

    @include min-width(md) {
        padding-block-start: var(--spacing-05);
    }

    @include min-width(lg) {
        padding-block-start: var(--spacing-02);
    }

    &--multiple {
        padding-block-end: var(--spacing-12);

        @include min-width(lg) {
            padding-block-end: 0;
        }
    }

    &--no-header {
        padding-block-start: var(--spacing-05);
    }

    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__title {
        color: var(--text-primary);
        font-family: var(--font-primary);
        font-size: var(--font-h4);
        font-weight: var(--font-weight-medium);
        line-height: var(--line-height-150);
        padding-inline: var(--spacing-05);
        text-align: center;
    }

    &__container {
        display: grid;
        overflow: auto;
        block-size: 100%;
        gap: var(--spacing-02);
        grid-template-columns: repeat(2, 1fr);
        inline-size: 100%;
        padding-block: var(--spacing-04) var(--spacing-07);
        padding-inline: var(--spacing-04);

        @include min-width(md) {
            grid-template-columns: repeat(3, 1fr);
            padding-block: var(--spacing-07);
            padding-inline: var(--spacing-07);
        }

        @include min-width(lg) {
            grid-template-columns: repeat(5, 1fr);
            padding-inline: var(--spacing-12);
        }

        &--wide {
            @include min-width(lg) {
                gap: var(--spacing-03);
                padding-block-end: var(--spacing-16);
            }
        }

        &--centered {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            padding-inline: var(--spacing-02);

            @include min-width(lg) {
                padding-inline: var(--spacing-12);
            }

            :deep(.small-card) {
                max-inline-size: 156px;

                @include min-width(md) {
                    max-inline-size: 196px;
                }
            }

        }
    }

    &__next,
    &__skip {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        align-self: center;
        inset-block-end: var(--spacing-05);
        inset-inline-start: 50%;
        min-inline-size: 174px;
        transform: translateX(-50%);

        @include min-width(lg) {
            margin-block-end: var(--spacing-06);
            transform: none;
        }
    }

    &__next {
        @include min-width(lg) {
            position: static;
            inset-inline-start: 0;
        }
    }

    &__skip {
        @include min-width(lg) {
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            margin-block-end: 0;
        }
    }
}
</style>
